import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import {
  PlayQuestion,
  PlayQuestionFormValues,
  PlayQuestionType,
} from "./web_parts/types";

const COLLECTION = "play_questions";

export class PlayQuestionService {
  private static normalize(data: Record<string, unknown>, id: string): PlayQuestion | null {
    const typeRaw = String(data.type ?? "").trim().toLowerCase();
    const type: PlayQuestionType =
      typeRaw === "quiz" ? "quiz" : typeRaw === "riddle" ? "riddle" : "riddle";
    if (typeRaw && typeRaw !== "quiz" && typeRaw !== "riddle") {
      return null;
    }

    const enabledObj = data.enabled;
    let enabled = true;
    if (typeof enabledObj === "boolean") {
      enabled = enabledObj;
    } else if (typeof enabledObj === "number") {
      enabled = enabledObj !== 0;
    }

    const correctIndex =
      typeof data.correctIndex === "number"
        ? data.correctIndex
        : Number(data.correctIndex ?? 0) || 0;
    const sortOrder =
      typeof data.sortOrder === "number"
        ? data.sortOrder
        : Number(data.sortOrder ?? 0) || 0;

    return {
      id,
      type,
      question: String(data.question ?? "").trim(),
      answer: String(data.answer ?? "").trim(),
      hint: String(data.hint ?? "").trim(),
      choice1: String(data.choice1 ?? "").trim(),
      choice2: String(data.choice2 ?? "").trim(),
      choice3: String(data.choice3 ?? "").trim(),
      correctIndex,
      genre: String(data.genre ?? "").trim(),
      enabled,
      sortOrder,
    };
  }

  private static toFirestorePayload(
    type: PlayQuestionType,
    values: PlayQuestionFormValues
  ): {
    type: PlayQuestionType;
    question: string;
    answer: string;
    hint: string;
    choice1: string;
    choice2: string;
    choice3: string;
    correctIndex: number;
    genre: string;
    enabled: boolean;
    sortOrder: number;
  } {
    const correctIndex =
      type === "quiz" ? Number(values.correctIndex ?? 1) : 0;
    const choices = {
      choice1: type === "quiz" ? String(values.choice1 ?? "").trim() : "",
      choice2: type === "quiz" ? String(values.choice2 ?? "").trim() : "",
      choice3: type === "quiz" ? String(values.choice3 ?? "").trim() : "",
    };

    let answer = String(values.answer ?? "").trim();
    if (type === "quiz" && !answer) {
      const map: Record<number, string> = {
        1: choices.choice1,
        2: choices.choice2,
        3: choices.choice3,
      };
      answer = map[correctIndex] ?? "";
    }

    return {
      type,
      question: String(values.question ?? "").trim(),
      answer,
      hint: String(values.hint ?? "").trim(),
      ...choices,
      correctIndex,
      genre: type === "quiz" ? String(values.genre ?? "").trim() : "",
      enabled: values.enabled !== false,
      sortOrder: Number(values.sortOrder ?? 0) || 0,
    };
  }

  static async getByType(type: PlayQuestionType): Promise<PlayQuestion[]> {
    const q = query(collection(db, COLLECTION), where("type", "==", type));
    const snapshot = await getDocs(q);
    const list = snapshot.docs
      .map((d) => this.normalize(d.data() as Record<string, unknown>, d.id))
      .filter((item): item is PlayQuestion => item != null && item.type === type);
    list.sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
    return list;
  }

  static async create(
    type: PlayQuestionType,
    values: PlayQuestionFormValues
  ): Promise<string> {
    const id =
      String(values.id ?? "").trim() ||
      `${type}_${Date.now()}`;
    const existing = await getDoc(doc(db, COLLECTION, id));
    if (existing.exists()) {
      throw new Error(`ID「${id}」は既に登録されています`);
    }
    const payload = this.toFirestorePayload(type, values);
    await setDoc(doc(db, COLLECTION, id), payload);
    return id;
  }

  static async update(
    id: string,
    type: PlayQuestionType,
    values: PlayQuestionFormValues
  ): Promise<void> {
    const payload = this.toFirestorePayload(type, values);
    await updateDoc(doc(db, COLLECTION, id), payload);
  }

  static async remove(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  }

  static nextSortOrder(items: PlayQuestion[]): number {
    if (items.length === 0) return 1;
    return Math.max(...items.map((i) => i.sortOrder)) + 1;
  }
}
